import { useState } from 'react'
import { useTheme } from '../theme'
import { Button, Card, Input, Badge, Modal } from '../components/ui'

const DESIGN_PROPOSALS = [
  {
    id: 'A',
    name: 'Liste Classique',
    file: '/design/A-glass-morphism.html',
    color: 'from-green-500 to-emerald-600',
    description: 'Structure: Liste verticale simple. Hero banner + cards empilées.'
  },
  {
    id: 'B',
    name: 'Calendrier Central',
    file: '/design/B-neumorphism.html',
    color: 'from-violet-500 to-purple-600',
    description: 'Structure: Calendrier interactif au centre.'
  },
  {
    id: 'C',
    name: 'Cartes Swipe',
    file: '/design/C-brutalist-tropical.html',
    color: 'from-amber-500 to-orange-600',
    description: 'Structure: Carousels horizontaux swipeable.'
  },
  {
    id: 'D',
    name: 'Bento Dashboard',
    file: '/design/D-minimal-card.html',
    color: 'from-slate-700 to-slate-900',
    description: 'Structure: Grille Bento avec stats.'
  },
  {
    id: 'E',
    name: 'Focus Produits',
    file: '/design/E-tendances-2026.html',
    color: 'from-emerald-500 to-teal-600',
    description: 'Structure: Recherche produits en priorité.'
  },
  {
    id: 'F',
    name: 'Marché Immersif',
    file: '/design/F-marche-immersif.html',
    color: 'from-orange-500 to-green-600',
    description: 'Expérience immersive: Graine de Vie, portail souveraineté. (ACTIF)'
  },
  {
    id: 'G',
    name: 'Design Précédent',
    file: '/design/G-design-actuel.html',
    color: 'from-orange-400 to-emerald-500',
    description: 'Le design précédent du portail.'
  }
]

function PageDesign({ onBack }) {
  const [selectedDesign, setSelectedDesign] = useState(null)
  const [modalVariant, setModalVariant] = useState(null)
  const { theme, toggleTheme } = useTheme()

  return (
    <div className="min-h-screen pt-6 pb-20 px-4 relative z-10">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={onBack}
          className="mb-6 text-primary hover:text-[#f39c12] font-medium flex items-center gap-2"
        >
          ← Retour au marché
        </button>

        <h2 className="font-display text-2xl font-bold text-white mb-2">
          Propositions Design UX
        </h2>
        <p className="text-white/60 mb-8">
          Historique des directions visuelles explorées
        </p>

        {/* ===== Design System — vitrine live des primitives + tokens ===== */}
        <div className="mb-12">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <h3 className="font-display text-lg font-semibold text-white">
              Design System — composants live
            </h3>
            <button
              onClick={toggleTheme}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-white/10 border border-white/15 text-white hover:bg-white/15 transition"
            >
              Theme : {theme === 'web3' ? 'Web3 (sombre)' : 'Web2 (clair)'} — basculer
            </button>
          </div>

          <div className="bg-bg border border-card-border rounded-3xl p-6 space-y-6">
            <div>
              <p className="text-xs font-medium text-ink/60 mb-2">Boutons</p>
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="primary">Primaire</Button>
                <Button variant="secondary">Secondaire</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="danger">Danger</Button>
                <Button variant="icon" aria-label="Exemple icone">★</Button>
              </div>
            </div>

            <div>
              <p className="text-xs font-medium text-ink/60 mb-2">Cartes</p>
              <div className="grid gap-3 sm:grid-cols-3">
                <Card className="p-4">
                  <p className="text-ink font-semibold text-sm">Default</p>
                  <p className="text-ink/60 text-xs mt-1">Surface translucide</p>
                </Card>
                <Card variant="glass" className="p-4">
                  <p className="text-ink font-semibold text-sm">Glass</p>
                  <p className="text-ink/60 text-xs mt-1">Liquid glass</p>
                </Card>
                <Card variant="solid" className="p-4">
                  <p className="text-ink font-semibold text-sm">Solid</p>
                  <p className="text-ink/60 text-xs mt-1">Opaque</p>
                </Card>
              </div>
            </div>

            <div>
              <p className="text-xs font-medium text-ink/60 mb-2">Badges</p>
              <div className="flex flex-wrap gap-2">
                <Badge tone="neutral">Neutre</Badge>
                <Badge tone="primary">Primaire</Badge>
                <Badge tone="green">Vert</Badge>
                <Badge tone="danger">Danger</Badge>
                <Badge tone="info">Info</Badge>
              </div>
            </div>

            <div className="max-w-xs">
              <Input label="Champ exemple" placeholder="Saisir du texte..." />
            </div>

            <div className="flex flex-wrap gap-3">
              <Button variant="secondary" onClick={() => setModalVariant('center')}>
                Ouvrir modale
              </Button>
              <Button variant="ghost" onClick={() => setModalVariant('sheet')}>
                Ouvrir bottom-sheet
              </Button>
            </div>

            <div>
              <p className="text-xs font-medium text-ink/60 mb-2">Tokens couleurs</p>
              <div className="flex flex-wrap gap-3 text-xs text-ink/70">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-xl bg-primary mb-1" />
                  primary
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-xl bg-green mb-1" />
                  green
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-xl bg-ink mb-1" />
                  ink
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-xl grad-primary mb-1" />
                  grad-primary
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-xl grad-accent mb-1" />
                  grad-accent
                </div>
              </div>
            </div>
          </div>
        </div>

        <Modal
          open={!!modalVariant}
          onClose={() => setModalVariant(null)}
          title="Modale exemple"
          variant={modalVariant === 'sheet' ? 'sheet' : 'center'}
        >
          <p className="text-ink/70 text-sm">
            Contenu de la modale (surface liquid glass). Echap ou clic exterieur pour fermer.
          </p>
          <Button variant="primary" fullWidth className="mt-4" onClick={() => setModalVariant(null)}>
            Fermer
          </Button>
        </Modal>

        {!selectedDesign ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {DESIGN_PROPOSALS.map((design) => (
              <button
                key={design.id}
                onClick={() => setSelectedDesign(design)}
                className="text-left p-6 bg-white/5 border border-white/10 rounded-2xl hover:border-primary/30 hover:bg-white/10 transition-all group"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${design.color} flex items-center justify-center text-white font-bold text-xl mb-4 group-hover:scale-110 transition-transform`}>
                  {design.id}
                </div>
                <h3 className="font-display font-semibold text-white text-lg mb-2">
                  {design.name}
                </h3>
                <p className="text-sm text-white/60">
                  {design.description}
                </p>
              </button>
            ))}
          </div>
        ) : (
          <div>
            <button
              onClick={() => setSelectedDesign(null)}
              className="mb-4 text-primary hover:text-[#f39c12] font-medium"
            >
              ← Retour aux propositions
            </button>
            <div className="bg-black/50 rounded-2xl overflow-hidden border border-white/10">
              <div className="bg-white/5 px-4 py-2 flex items-center gap-2 border-b border-white/10">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <span className="text-white/60 text-xs ml-2">{selectedDesign.name}</span>
              </div>
              <iframe
                src={selectedDesign.file}
                className="w-full h-[600px] md:h-[700px] bg-white"
                title={selectedDesign.name}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PageDesign
